import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

interface PaymentStats {
  paidCount: number;
  totalCollection: number;
  paymentAmount: number;
  lastPayment: {
    applicationId: string;
    applicantName: string;
    paidAt: string;
    amount: number;
  } | null;
}

interface PaidApplicationsCardProps {
  totalApplications: number;
  onNewPayment?: (payment: PaymentStats['lastPayment']) => void;
  onClick?: () => void;
  tooltip?: string;
}

// ─── Animations ──────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const softGlow = keyframes`
  0%, 100% { box-shadow: 0 6px 24px rgba(109, 40, 217, 0.12); }
  50% { box-shadow: 0 6px 24px rgba(109, 40, 217, 0.12), 0 0 28px 2px rgba(34, 197, 94, 0.18); }
`;

const liveDot = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 4px #22c55e; }
  50% { opacity: 0.4; box-shadow: 0 0 10px #22c55e; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

const CardContainer = styled(motion.div)<{ $hasNewPayment?: boolean }>`
  background: linear-gradient(155deg, #f8f5ff 0%, #f0ebff 35%, #ebe4ff 60%, #e8e0ff 100%);
  border-radius: 16px;
  padding: 1.15rem 1.15rem 1rem;
  display: flex;
  flex-direction: column;
  border: 1px solid #d8ccf0;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease, border-color 0.3s ease;

  ${props => props.$hasNewPayment
    ? css`animation: ${softGlow} 2s ease-in-out infinite;`
    : `box-shadow: 0 6px 24px rgba(109, 40, 217, 0.10);`
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 40px rgba(109, 40, 217, 0.18);
    border-color: #c4b5fd;
  }

  /* Top accent shimmer bar */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7c3aed, #a78bfa, #c084fc, #7c3aed);
    background-size: 300% 100%;
    animation: ${shimmer} 3s linear infinite;
  }

  /* Bottom-right decorative glow orb */
  &::after {
    content: '';
    position: absolute;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.12) 0%, transparent 70%);
    bottom: -50px;
    right: -30px;
    pointer-events: none;
  }
`;

/* ─── Header ─── */

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 1;
`;

const TitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const CoinIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7c3aed 0%, #9333ea 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 800;
  color: #fff;
  box-shadow: 0 3px 10px rgba(124, 58, 237, 0.35);
  animation: ${float} 3s ease-in-out infinite;
`;

const Title = styled.p`
  color: #3b1f7a;
  font-size: 0.82rem;
  margin: 0;
  font-weight: 700;
`;

/* ─── Count Badge ─── */

const CountBadge = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(196, 181, 253, 0.5);
  border-radius: 12px;
  padding: 6px 13px;
`;

const CountIconCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
`;

const CountIconSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CountNumber = styled.span`
  font-size: 1.2rem;
  font-weight: 800;
  color: #3b1f7a;
  line-height: 1;
`;

/* ─── Amount ─── */

const AmountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-bottom: 3px;
  position: relative;
  z-index: 1;
`;

const CurrencySymbol = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #8b5cf6;
`;

const AmountValue = styled(motion.span)`
  font-size: 2.3rem;
  font-weight: 800;
  letter-spacing: -1px;
  color: #2e1065;
  line-height: 1.1;
`;

const SubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 0.85rem;
  position: relative;
  z-index: 1;
`;

const SubText = styled.span`
  font-size: 0.68rem;
  color: #7c6caa;
`;

const PerUnitBadge = styled.span`
  font-size: 0.6rem;
  color: #6d28d9;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(196, 181, 253, 0.4);
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 600;
`;

/* ─── Conversion bar ─── */

const ConversionSection = styled.div`
  margin-bottom: 0.85rem;
  position: relative;
  z-index: 1;
`;

const ConversionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
`;

const ConversionLabel = styled.span`
  font-size: 0.62rem;
  color: #7c6caa;
  font-weight: 500;
`;

const ConversionPercent = styled.span`
  font-size: 0.68rem;
  color: #6d28d9;
  font-weight: 800;
`;

const ConversionTrack = styled.div`
  width: 100%;
  height: 7px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(196, 181, 253, 0.25);
`;

const ConversionFill = styled(motion.div)<{ $percent: number }>`
  height: 100%;
  width: ${p => Math.max(p.$percent, 0.8)}%;
  background: linear-gradient(90deg, #7c3aed, #a78bfa, #c084fc);
  border-radius: 4px;
  min-width: 4px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
    background-size: 200% 100%;
    animation: ${shimmer} 2s linear infinite;
  }
`;

/* ─── Bottom stats ─── */

const StatsBar = styled.div`
  display: flex;
  align-items: stretch;
  margin-top: auto;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(4px);
  border-radius: 10px;
  border: 1px solid rgba(196, 181, 253, 0.3);
  overflow: hidden;
  position: relative;
  z-index: 1;
`;

const StatItem = styled.div<{ $border?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  ${props => props.$border && `border-right: 1px solid rgba(196, 181, 253, 0.25);`}
`;

const StatValue = styled.span<{ $color?: string }>`
  font-size: 0.95rem;
  font-weight: 800;
  color: ${p => p.$color || '#2e1065'};
  line-height: 1.1;
`;

const StatLabel = styled.span`
  font-size: 0.52rem;
  color: #8b7ab8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
`;

/* ─── New Payment Indicator ─── */

const NewPaymentBadge = styled(motion.div)`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(240, 253, 244, 0.9);
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 3px 9px;
  z-index: 2;
`;

const LiveDotEl = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  animation: ${liveDot} 1.5s ease-in-out infinite;
`;

const NewPaymentText = styled.span`
  font-size: 0.55rem;
  color: #16a34a;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

/* ─── Hover Tooltip ─── */

const TooltipOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(30, 27, 75, 0.93);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem 1.3rem;
  z-index: 10;
  animation: ${fadeIn} 0.2s ease-out;
`;

const TooltipText = styled.p`
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.72rem;
  line-height: 1.6;
  margin: 0;
  text-align: center;
  font-weight: 400;
  letter-spacing: 0.1px;
`;

// ─── Animated counter hook ─────────────────────────────────────────────────

const useAnimatedCounter = (targetValue: number, duration: number = 2000) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousValue = useRef(0);
  const animationRef = useRef<number>();

  useEffect(() => {
    const startValue = previousValue.current;
    const difference = targetValue - startValue;
    if (difference === 0) return;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(startValue + difference * easeOut);
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousValue.current = targetValue;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [targetValue, duration]);

  return displayValue;
};

const formatCurrency = (value: number): string => value.toLocaleString('en-IN');

const formatTimeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

// ─── Component ───────────────────────────────────────────────────────────────

const PaidApplicationsCard: React.FC<PaidApplicationsCardProps> = ({
  totalApplications,
  onNewPayment,
  onClick,
  tooltip
}) => {
  const [stats, setStats] = useState<PaymentStats>({
    paidCount: 0,
    totalCollection: 0,
    paymentAmount: 5000,
    lastPayment: null
  });
  const [showTooltip, setShowTooltip] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hasNewPayment, setHasNewPayment] = useState(false);
  const lastPaymentIdRef = useRef<string | null>(null);
  const animatedAmount = useAnimatedCounter(stats.totalCollection, 2500);
  const animatedCount = useAnimatedCounter(stats.paidCount, 1800);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/payment-stats`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            if (data.lastPayment &&
                lastPaymentIdRef.current !== null &&
                data.lastPayment.applicationId !== lastPaymentIdRef.current) {
              setHasNewPayment(true);
              onNewPayment?.(data.lastPayment);
              setTimeout(() => setHasNewPayment(false), 5000);
            }

            if (data.lastPayment) {
              lastPaymentIdRef.current = data.lastPayment.applicationId;
            }

            setStats({
              paidCount: data.paidCount,
              totalCollection: data.totalCollection,
              paymentAmount: data.paymentAmount,
              lastPayment: data.lastPayment
            });
          }
        }
      } catch (error) {
        console.error('[PaidApplicationsCard] Error fetching stats:', error);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, [onNewPayment]);

  const conversionPercent = totalApplications > 0
    ? parseFloat(((stats.paidCount / totalApplications) * 100).toFixed(1))
    : 0;
  const remaining = Math.max(0, totalApplications - stats.paidCount);

  const handleMouseEnter = () => {
    if (!tooltip) return;
    hoverTimerRef.current = setTimeout(() => setShowTooltip(true), 600);
  };
  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setShowTooltip(false);
  };

  return (
    <CardContainer
      onClick={onClick}
      $hasNewPayment={hasNewPayment}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <AnimatePresence>
        {showTooltip && tooltip && (
          <TooltipOverlay>
            <TooltipText>{tooltip}</TooltipText>
          </TooltipOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hasNewPayment && (
          <NewPaymentBadge
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            <LiveDotEl />
            <NewPaymentText>New Payment</NewPaymentText>
          </NewPaymentBadge>
        )}
      </AnimatePresence>

      {/* Header */}
      <Header>
        <TitleArea>
          <CoinIcon>₹</CoinIcon>
          <Title>Paid Applications</Title>
        </TitleArea>
        <CountBadge
          key={stats.paidCount}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <CountIconCircle><CountIconSvg /></CountIconCircle>
          <CountNumber>{animatedCount}</CountNumber>
        </CountBadge>
      </Header>

      {/* Amount */}
      <AmountRow>
        <CurrencySymbol>₹</CurrencySymbol>
        <AmountValue
          key={stats.totalCollection}
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
        >
          {formatCurrency(animatedAmount)}
        </AmountValue>
      </AmountRow>
      <SubRow>
        <SubText>Total Collection</SubText>
        <PerUnitBadge>₹{formatCurrency(stats.paymentAmount)} / applicant</PerUnitBadge>
      </SubRow>

      {/* Conversion bar with label */}
      <ConversionSection>
        <ConversionHeader>
          <ConversionLabel>Conversion Rate</ConversionLabel>
          <ConversionPercent>{conversionPercent}%</ConversionPercent>
        </ConversionHeader>
        <ConversionTrack>
          <ConversionFill
            $percent={conversionPercent}
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(conversionPercent, 0.8)}%` }}
            transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
          />
        </ConversionTrack>
      </ConversionSection>

      {/* Bottom 3-col stats */}
      <StatsBar>
        <StatItem $border>
          <StatValue $color="#6d28d9">{stats.paidCount}<span style={{ fontSize: '0.6rem', color: '#8b7ab8', fontWeight: 500 }}> / {totalApplications}</span></StatValue>
          <StatLabel>Paid</StatLabel>
        </StatItem>
        <StatItem $border>
          <StatValue>{remaining}</StatValue>
          <StatLabel>Remaining</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue style={{ fontSize: '0.7rem', color: '#4c1d95', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90px' }}>
            {stats.lastPayment
              ? (stats.lastPayment.applicantName || stats.lastPayment.applicationId.slice(-6))
              : '—'}
          </StatValue>
          <StatLabel>Latest{stats.lastPayment ? ` · ${formatTimeAgo(stats.lastPayment.paidAt)}` : ''}</StatLabel>
        </StatItem>
      </StatsBar>
    </CardContainer>
  );
};

export default PaidApplicationsCard;
